import { differenceInDays } from 'date-fns';
import { deepEqual, get, intersection, type Nullable } from '@direct-frontend/stdlib';
import {
    createForm,
    createPrimitiveField,
    type FormApi,
    type InferFormFields,
    type InferFormValue,
    literal,
    nullable,
    type ValidationError,
} from '@adv-frontend/form-kit';
import { createReaction } from '@adv-frontend/reactions';
import {
    deliveryClientNameRegex,
    getOrderDeliveryOptions,
    InvalidOrderError,
    MinimumOrderAmountNotMetError,
    OrderMismatchedDeliveryPriceError,
    OrderMismatchedItemPriceError,
    OrderPaymentError,
    OrderReserveStocksError,
    proceedOrderPayment,
    PromoCodeUsageLimitExceededError,
    updateOrderSnapshot,
    upsertDeliveryOptions,
} from '@/entities/order';
import {
    OrderInvalidDeliveryFloorError,
    OrderInvalidEmailError,
    OrderInvalidPhoneError,
    OrderLoyaltyApplyError,
    OrderLoyaltyConditionsChangedError,
} from '@/entities/order';
import { trackGoal } from '@/entities/trackGoal';
// eslint-disable-next-line @direct-frontend/modules/slice-boundaries -- Уберем форму клинта в фичу ордер формы
import { CLIENT_FORM_ID, clientForm } from '@/features/ClientPicker';
// eslint-disable-next-line @direct-frontend/modules/slice-boundaries -- Уберем форму адреса доставки в фичу ордер формы
import { COURIER_ADDRESS_FORM_ID, courierAddressForm } from '@/features/CourierAddressPicker';
import {
    type OrderDeliveryMethod,
    type OrderDeliveryOptions,
    type OrderDto,
    type OrderPaymentMethod,
    OrderPostPaymentMethods,
    type ServiceType,
} from '@/shared/api/order';
import { OrderDeliveryMethods, OrderPaymentMethods } from '@/shared/api/order';
import { arrayIntervalField } from '@/shared/components/IntervalInput';
import { getDefaultPaymentMethod } from '@/shared/lib/getDefaultPaymentMethod';
import { modalsService } from '@/shared/lib/modalsService';
import { ORDER_FORM_ID } from '../../constants';
import { getAvailablePaymentMethods } from '../../lib/getAvailablePaymentMethods';
import { getIntervalValues } from '../../lib/getIntervalValues';
import { getOrderItemsSignature } from '../../lib/getOrderItemsSignature';
import { getIsInvalidOzonUser } from '../../lib/hooks/useIsInvalidOzonUser';
import { fromFormValues } from '../../lib/mappers/fromFormValues';
import { reinitializeOrderForm } from '../../lib/reinitializeOrderForm';
import { fieldClient } from './fieldClient';
import { fieldCourierAddress } from './fieldCourierAddress';
import { fieldPickupAddress } from './fieldPickupAddress';
import { fieldSelfPickUpAddress } from './fieldSelfPickUpAddress';

export type OrderFormContext = {
    orderId: string;
    signature: string;
    storeSlug: string;
    storeUrl?: string;
    order: OrderDto;
    deliveryOptions: {
        orderDeliveryOptions: OrderDeliveryOptions;
        isLoading: boolean;
    };
    deliveryIntervalsChunks: Partial<
        Record<
            OrderDeliveryMethod,
            Array<{
                chunkId: number;
                intervals: Array<{
                    from: number;
                    to: number;
                    serviceType: ServiceType;
                    price: Nullable<string>;
                    currency: Nullable<string>;
                }>;
            }>
        >
    >;
    paymentMethods: {
        items: OrderPaymentMethod[];
        isLoading: boolean;
    };
    refetch: () => Promise<unknown>;
};

type OrderFormValidationError = ValidationError<'REQUIRED_LOCALITY' | 'PAYMENT_FAILED' | 'REQUIRED_OZON_ID'>;

const getFastestDeliveryDays = (deliveryMethods: OrderDeliveryOptions['deliveryMethods']) =>
    Math.min(
        deliveryMethods.courier?.interval.from || Infinity,
        deliveryMethods.pickupPoint?.interval.from || Infinity,
        deliveryMethods.selfPickupPoint?.interval.from || Infinity
    );

const updateDeliveryOptions = async (params: { condition: boolean; forceRefetch?: boolean }) => {
    const { condition, forceRefetch } = params;
    const instance = orderForm.getInstance(ORDER_FORM_ID);

    if (!instance.isInitialized() || !condition) {
        return;
    }

    const locality = instance.fields.locality.getValue();
    const deliveryMethod = instance.fields.deliveryMethod.getValue() as Nullable<OrderDeliveryMethod>;
    const addressPickup = instance.fields.addressPickup.getValue();
    const addressCourier = instance.fields.addressCourier.getValue();

    instance.setContext({
        ...instance.getContext(),
        deliveryOptions: {
            orderDeliveryOptions: {
                deliveryMethods: {},
                deliveryChunksOptions: [],
                isDeliveryChunksChanged: false,
            },
            isLoading: true,
        },
    });

    try {
        const getDeliveryOptionsParams: Parameters<typeof getOrderDeliveryOptions>[0] =
            deliveryMethod === 'COURIER'
                ? {
                      orderId: instance.getContext().orderId,
                      storeSlug: instance.getContext().storeSlug,
                      locality: addressCourier.locality || locality,
                      detailAddress: addressCourier.address,
                  }
                : {
                      orderId: instance.getContext().orderId,
                      storeSlug: instance.getContext().storeSlug,
                      locality: addressPickup.locality || locality,
                      pickupPointId: addressPickup.pickupPointId,
                      serviceType: addressPickup.serviceType,
                      detailAddress: addressPickup.address,
                  };
        const orderDeliveryOptions = await getOrderDeliveryOptions(getDeliveryOptionsParams, { forceRefetch });

        if (orderDeliveryOptions.isDeliveryChunksChanged) {
            const prevOrder = instance.getContext().order;
            const updatedOrder = await updateOrderSnapshot({
                order_id: instance.getContext().orderId,
                store_slug: instance.getContext().storeSlug,
                signature: instance.getContext().signature,
            });

            reinitializeOrderForm(prevOrder, updatedOrder);

            if (updatedOrder.delivery_options) {
                upsertDeliveryOptions(getDeliveryOptionsParams, updatedOrder.delivery_options);
            }
        }

        const fastestDeliveryDays = getFastestDeliveryDays(orderDeliveryOptions.deliveryMethods);

        if (isFinite(fastestDeliveryDays)) {
            trackGoal('delivery_details_loaded', {
                fastest_delivery_days: differenceInDays(new Date(fastestDeliveryDays * 1000), new Date()) + 1,
            });
        }

        instance.setContext({
            ...instance.getContext(),
            deliveryOptions: { orderDeliveryOptions: orderDeliveryOptions, isLoading: false },
        });
    } catch (error) {
        /**
         * У реакций есть ограничение в 20 на количество запусков экземпляра реакции   -
         * https://a.yandex-team.ru/arcadia/adv/frontend/packages/reactions/src/createReaction/reactionMonitoring.ts?rev=ed8c81bd31d4f325d0fb288a9114c96b93946358#L17
         * При привышении данного ограничения падает ошибка во всех, даже уже запущеных реакциях данного инстанса
         * Не обращаем внимания на данную ошибку, иначе deliveryOptions в контексте будут перезатираться
         */

        if (get(error, ['name']) === 'NotifiesLimitReachedError') {
            return;
        }

        instance.setContext({
            ...instance.getContext(),
            deliveryOptions: {
                orderDeliveryOptions: {
                    deliveryMethods: {},
                    deliveryChunksOptions: [],
                    isDeliveryChunksChanged: false,
                },
                isLoading: false,
            },
        });
    }

    const orderDeliveryOptions = instance.getContext().deliveryOptions.orderDeliveryOptions;

    const intervalsChunks = orderDeliveryOptions.deliveryChunksOptions.map((chunk) => {
        return {
            chunkId: chunk.chunkId,
            intervals: chunk.options.find((option) => option.method === deliveryMethod)?.intervals ?? [],
        };
    });

    let prevInterval = null;

    if (deliveryMethod === OrderDeliveryMethods.COURIER) {
        prevInterval = instance.fields.deliveryCourierInterval.getValue();
    }

    if (deliveryMethod === OrderDeliveryMethods.PICKUP_POINT) {
        prevInterval = instance.fields.deliveryPickupPointInterval.getValue();
    }

    if (deliveryMethod === OrderDeliveryMethods.SELF_PICK_UP) {
        prevInterval = instance.fields.deliverySelfPickUpInterval.getValue();
    }

    if (deliveryMethod) {
        instance.setContext({
            ...instance.getContext(),
            deliveryIntervalsChunks: {
                ...instance.getContext().deliveryIntervalsChunks,
                [deliveryMethod]: intervalsChunks.map((chunk) => {
                    return {
                        chunkId: chunk.chunkId,
                        intervals: chunk.intervals.map((interval) => {
                            return {
                                type: 'interval',
                                from: interval.from,
                                to: interval.to,
                                serviceType: interval.serviceType,
                                price: interval.price ? interval.price.price : null,
                                currency: interval.price ? interval.price?.currency : null,
                            };
                        }),
                    };
                }),
            },
        });
    }

    const selectedIntervals = getIntervalValues(
        intervalsChunks,
        prevInterval,
        deliveryMethod,
        addressCourier.address,
        addressPickup.pickupPointId
    );

    if (deliveryMethod === OrderDeliveryMethods.COURIER) {
        // Установка значения после асинхронной логики должна быть описана в асинхронном экшене: DIRECT-302840
        // eslint-disable-next-line @direct-frontend/modules/form-kit-require-async-action -- см. выше
        instance.fields.deliveryCourierInterval.actions.setValues(selectedIntervals);
    }

    if (deliveryMethod === OrderDeliveryMethods.PICKUP_POINT) {
        // Установка значения после асинхронной логики должна быть описана в асинхронном экшене: DIRECT-302840
        // eslint-disable-next-line @direct-frontend/modules/form-kit-require-async-action -- см. выше
        instance.fields.deliveryPickupPointInterval.actions.setValues(selectedIntervals);
    }
};

export const validateOrderForm = (
    values: OrderFormValues,
    formApiParam?: FormApi<InferFormFields<typeof orderForm>, OrderFormValidationError, OrderFormContext>
) => {
    const instance = orderForm.getInstance(ORDER_FORM_ID);
    const context = instance.getContext();

    const formApi = formApiParam ?? instance;

    trackGoal(values.paymentMethod === OrderPaymentMethods.CARD_ONLINE ? 'proceed_payment' : 'place_an_order');

    if (!values.locality) {
        formApi.addValidationError({ code: 'REQUIRED_LOCALITY' });
    }

    if (!values.deliveryMethod) {
        formApi.fields.deliveryMethod.addValidationError({ code: 'REQUIRED_DELIVERY_METHOD' });
    }

    if (values.deliveryMethod === OrderDeliveryMethods.COURIER) {
        if (!values.addressCourier.address) {
            formApi.fields.addressCourier.addValidationError({ code: 'REQUIRED_DELIVERY_ADDRESS' });
        } else if (
            values.deliveryCourierInterval.some((interval) => {
                const chunkData = context.order.delivery_chunks.find((c) => c.id === interval.chunkId);

                return chunkData?.is_available && interval.intervalField.type === 'notInterval';
            })
        ) {
            formApi.fields.addressCourier.addValidationError({ code: 'UNSUPPORTED_ADDRESS' });
        }
    }

    if (values.deliveryMethod === OrderDeliveryMethods.PICKUP_POINT) {
        if (!values.addressPickup.address || !values.addressPickup.pickupPointId) {
            formApi.fields.addressPickup.addValidationError({ code: 'REQUIRED_DELIVERY_ADDRESS' });
        } else if (
            values.deliveryPickupPointInterval.some((interval) => {
                const chunkData = context.order.delivery_chunks.find((c) => c.id === interval.chunkId);

                return chunkData?.is_available && interval.intervalField.type === 'notInterval';
            })
        ) {
            formApi.fields.addressPickup.addValidationError({ code: 'UNSUPPORTED_ADDRESS' });
        }
    }

    if (
        values.deliveryMethod === OrderDeliveryMethods.SELF_PICK_UP &&
        (!values.addressSelfPickUp.address || !values.addressSelfPickUp.pickUpWarehouseId)
    ) {
        formApi.fields.addressSelfPickUp.addValidationError({ code: 'REQUIRED_DELIVERY_ADDRESS' });
    }

    if (!values.client.firstName || !values.client.lastName || !values.client.phone || !values.client.email) {
        formApi.fields.client.addValidationError({ code: 'REQUIRED_CLIENT' });
    }

    const isInvalidOzonUser = getIsInvalidOzonUser({
        type: 'form',
        isUserStatusInvalid: context.order.client.need_ozon_registration,
        method: values.deliveryMethod,
        fieldAddressPickupValue: values.addressPickup,
        courierIntervals: formApi.fields.deliveryCourierInterval.selectors.getValues(),
    });

    if (isInvalidOzonUser) {
        formApi.addValidationError({ code: 'REQUIRED_OZON_ID' });
        modalsService.onOzonRegistrationError();
    }

    const isFirstNameValid = deliveryClientNameRegex.test(values.client.firstName);
    const isLastNameValid = deliveryClientNameRegex.test(values.client.lastName);

    if (!isFirstNameValid || !isLastNameValid) {
        formApi.fields.client.addValidationError({ code: 'INVALID_NAME_FORMAT' });

        const clientFormInstance = clientForm.getInstance(CLIENT_FORM_ID);

        if (!isFirstNameValid && values.client.firstName) {
            clientFormInstance.fields.firstName.addValidationError({ code: 'INVALID_FIRST_NAME_FORMAT' });
        }

        if (!isLastNameValid && values.client.lastName) {
            clientFormInstance.fields.lastName.addValidationError({ code: 'INVALID_LAST_NAME_FORMAT' });
        }
    }

    if (context.order.client.need_phone_verification) {
        formApi.fields.client.addValidationError({ code: 'REQUIRED_PHONE_VERIFICATION' });
    }

    if (!values.paymentMethod) {
        formApi.fields.paymentMethod.addValidationError({ code: 'REQUIRED_PAYMENT_METHOD' });
    }
};

export const orderForm = createForm({
    name: 'OrderForm',
    fields: {
        client: fieldClient,
        deliveryMethod: createPrimitiveField({
            defaultValue: nullable<OrderDeliveryMethod>(null),
            validators: (createValidators) => createValidators<ValidationError<'REQUIRED_DELIVERY_METHOD'>>({}),
        }),
        locality: createPrimitiveField({
            defaultValue: 'Москва',
        }),
        paymentMethod: createPrimitiveField({
            defaultValue: literal<OrderPaymentMethod>(OrderPaymentMethods.CARD_ONLINE),
            validators: (createValidators) => createValidators<ValidationError<'REQUIRED_PAYMENT_METHOD'>>({}),
        }),
        deliveryPickupPointInterval: arrayIntervalField,
        deliverySelfPickUpInterval: arrayIntervalField,
        deliveryCourierInterval: arrayIntervalField,
        addressCourier: fieldCourierAddress,
        addressPickup: fieldPickupAddress,
        addressSelfPickUp: fieldSelfPickUpAddress,
        promocode: createPrimitiveField({
            defaultValue: '',
        }),
        promocodeApply: createPrimitiveField({
            defaultValue: '',
        }),
        giftCardApply: createPrimitiveField({
            defaultValue: '',
        }),
        bonusesApplied: createPrimitiveField({
            defaultValue: false,
        }),
    },

    context: (formContext: OrderFormContext) => {
        return { form: formContext };
    },

    validators: (createValidators) =>
        createValidators<
            ValidationError<
                | 'REQUIRED_CLIENT'
                | 'REQUIRED_LOCALITY'
                | 'REQUIRED_DELIVERY_METHOD'
                | 'REQUIRED_DELIVERY_ADDRESS'
                | 'REQUIRED_DELIVERY_INTERVAL'
                | 'REQUIRED_PAYMENT_METHOD'
                | 'PAYMENT_FAILED'
                | 'INVALID_NAME_FORMAT'
                | 'REQUIRED_OZON_ID'
            >
        >({
            onSubmit: [
                (values, formApi) => {
                    validateOrderForm(values, formApi);
                },
            ],
        }),

    onSubmit: async (values, formApi) => {
        const context = formApi.getContext();
        const serialized = fromFormValues(context.orderId, values, context.order);
        const clientFormInstance = clientForm.getInstance(CLIENT_FORM_ID);
        const courierAddressFormInstance = courierAddressForm.getInstance(COURIER_ADDRESS_FORM_ID);

        if (!serialized) {
            return;
        }

        try {
            await proceedOrderPayment(
                serialized,
                context.signature,
                context.storeSlug,
                context.storeUrl,
                context.order
            );
            context.refetch();

            return { isSuccess: true };
        } catch (error) {
            trackGoal('payment_error');

            if (error instanceof OrderMismatchedItemPriceError) {
                modalsService.onOutdatedItemPrices();

                return;
            }

            if (error instanceof OrderReserveStocksError) {
                modalsService.onOutdatedStock(error.insufficientItems);

                return;
            }

            if (error instanceof OrderMismatchedDeliveryPriceError) {
                modalsService.onOutdatedDeliveryPrices();

                return;
            }

            if (error instanceof MinimumOrderAmountNotMetError) {
                modalsService.onMinimumOrderAmountNotMet();

                return;
            }

            if (error instanceof PromoCodeUsageLimitExceededError) {
                modalsService.onPromoCodeUsageLimitExceeded();

                return;
            }

            if (error instanceof OrderInvalidDeliveryFloorError) {
                formApi.fields.addressCourier.addValidationError({ code: 'REQUIRED_DELIVERY_FLOOR' });
                courierAddressFormInstance.fields.floor.addValidationError({ code: 'REQUIRED_DELIVERY_FLOOR' });
                modalsService.onInvalidDeliveryFloor();

                return;
            }

            if (error instanceof OrderInvalidEmailError) {
                formApi.fields.client.addValidationError({ code: 'INVALID_EMAIL_FORMAT' });
                clientFormInstance.fields.email.addValidationError({ code: 'INVALID_EMAIL_FORMAT' });
                modalsService.onInvalidEmail();

                return;
            }

            if (error instanceof OrderInvalidPhoneError) {
                formApi.fields.client.addValidationError({ code: 'INVALID_PHONE_FORMAT' });
                clientFormInstance.fields.phone.addValidationError({ code: 'INVALID_PHONE_FORMAT' });
                modalsService.onInvalidPhone();

                return;
            }

            if (error instanceof OrderLoyaltyApplyError) {
                modalsService.onLoyaltyApplyError();

                return;
            }

            if (error instanceof OrderLoyaltyConditionsChangedError) {
                modalsService.onOutdatedLoyaltyConditions();

                return;
            }

            if (error instanceof OrderPaymentError) {
                formApi.addValidationError({ code: 'PAYMENT_FAILED' });
            }

            const errorDescription = error instanceof InvalidOrderError ? error.errorDescription : undefined;

            modalsService.onSubmissionError(errorDescription);

            throw error;
        }
    },

    reactions: (createReactions) =>
        createReactions([
            // Изменение locality
            createReaction({
                *data(instance) {
                    return {
                        instance,
                        locality: instance.fields.locality.getValue(),
                    };
                },
                effect({ instance, locality }, reactionApi) {
                    if (!reactionApi.prevData || locality === reactionApi.prevData.locality) {
                        return;
                    }

                    // Сбрасываем способ доставки и адрес доставки ПВЗ, если не совпадает регион
                    if (instance.fields.addressPickup.getValue().locality !== locality) {
                        // Сбрасывать способ доставки стоит только, если он совпадает
                        if (instance.fields.deliveryMethod.getValue() === OrderDeliveryMethods.PICKUP_POINT) {
                            instance.fields.deliveryMethod.setValue(null);
                        }

                        instance.fields.addressPickup.setValue(fieldPickupAddress.defaultValue);
                    }

                    // Сбрасываем способ доставки и адрес доставки ПВЗ, если не совпадает регион
                    if (instance.fields.addressSelfPickUp.getValue().locality !== locality) {
                        // Сбрасывать способ доставки стоит только, если он совпадает
                        if (instance.fields.deliveryMethod.getValue() === OrderDeliveryMethods.SELF_PICK_UP) {
                            instance.fields.deliveryMethod.setValue(null);
                        }

                        instance.fields.addressSelfPickUp.setValue(fieldSelfPickUpAddress.defaultValue);
                    }

                    // Сбрасываем способ доставки и адрес доставки курьером, если не совпадает регион
                    if (instance.fields.addressCourier.getValue().locality !== locality) {
                        // Сбрасывать способ доставки стоит только, если он совпадает
                        if (instance.fields.deliveryMethod.getValue() === OrderDeliveryMethods.COURIER) {
                            instance.fields.deliveryMethod.setValue(null);
                        }

                        instance.fields.addressCourier.setValue(fieldCourierAddress.defaultValue);
                    }
                },
            }),

            // Изменение адреса доставки курьером
            createReaction({
                *data(instance) {
                    return {
                        instance,
                        addressCourier: instance.fields.addressCourier.getValue(),
                        deliveryMethod: instance.fields.deliveryMethod.getValue(),
                    };
                },
                effect({ instance, addressCourier, deliveryMethod }, reactionApi) {
                    if (
                        !reactionApi.prevData ||
                        addressCourier.address === reactionApi.prevData.addressCourier.address ||
                        deliveryMethod !== OrderDeliveryMethods.COURIER
                    ) {
                        return;
                    }

                    if (addressCourier.locality && instance.fields.locality.getValue() !== addressCourier.locality) {
                        instance.fields.locality.setValue(addressCourier.locality);
                    }

                    instance.fields.paymentMethod.setValue(getDefaultPaymentMethod());

                    instance.fields.deliveryCourierInterval.actions.setDefaultValue();
                },
            }),

            // Изменение адреса доставки ПВЗ
            createReaction({
                *data(instance) {
                    return {
                        instance,
                        addressPickup: instance.fields.addressPickup.getValue(),
                        deliveryMethod: instance.fields.deliveryMethod.getValue(),
                    };
                },
                effect({ instance, addressPickup, deliveryMethod }, reactionApi) {
                    if (
                        !reactionApi.prevData ||
                        addressPickup.address === reactionApi.prevData.addressPickup.address ||
                        deliveryMethod !== OrderDeliveryMethods.PICKUP_POINT
                    ) {
                        return;
                    }

                    if (addressPickup.locality && instance.fields.locality.getValue() !== addressPickup.locality) {
                        instance.fields.locality.setValue(addressPickup.locality);
                    }

                    instance.fields.paymentMethod.setValue(getDefaultPaymentMethod());
                    instance.fields.deliveryPickupPointInterval.actions.setDefaultValue();
                    instance.fields.deliverySelfPickUpInterval.actions.setDefaultValue();
                },
            }),

            // Актуализация доступных способов и интервалов доставки
            createReaction({
                *data(instance, reactionApi) {
                    reactionApi.stopDataSourcesSubscriptions();

                    const order = instance.getContext().order;

                    return {
                        orderDelivery: order.delivery,
                        deliveryMethod: order.delivery.method,
                        itemsSignature: getOrderItemsSignature(order.delivery_chunks),
                        paymentMethod: order.payment.method,
                        promocodeApply: order.promocode,
                        giftCardApply: order.gift_card,
                    };
                },
                async effect(
                    { orderDelivery, promocodeApply, giftCardApply, deliveryMethod, itemsSignature, paymentMethod },
                    reactionApi
                ) {
                    await reactionApi.waitPreviousReactions();

                    reactionApi.stopDataSourcesSubscriptions();

                    const isPromocodeChanged =
                        reactionApi.prevData && reactionApi.prevData.promocodeApply !== promocodeApply;
                    const isGiftCardChanged =
                        reactionApi.prevData && reactionApi.prevData.giftCardApply !== giftCardApply;
                    const isDeliveryMethodChanged =
                        reactionApi.prevData && reactionApi.prevData.deliveryMethod !== deliveryMethod;
                    const isItemsSignatureChanged =
                        reactionApi.prevData && reactionApi.prevData.itemsSignature !== itemsSignature;
                    const isPaymentMethodChanged =
                        reactionApi.prevData && reactionApi.prevData.paymentMethod !== paymentMethod;

                    const shouldUpdateOptions =
                        isDeliveryMethodChanged ||
                        isItemsSignatureChanged ||
                        isPaymentMethodChanged ||
                        isPromocodeChanged ||
                        isGiftCardChanged;

                    updateDeliveryOptions({
                        condition: Boolean(
                            !reactionApi.prevData ||
                                !deepEqual(reactionApi.prevData.orderDelivery, orderDelivery) ||
                                shouldUpdateOptions
                        ),
                        forceRefetch: shouldUpdateOptions,
                    });

                    if (isGiftCardChanged) {
                        const instance = reactionApi.param;

                        instance.fields.paymentMethod.setValue(getDefaultPaymentMethod());
                    }

                    reactionApi.startDataSourcesSubscriptions();
                },
            }),

            // Актуализация доступных способов оплаты
            createReaction({
                *data(instance) {
                    return {
                        instance,
                        addressPickup: instance.fields.addressPickup.getValue(),
                        addressSelfPickUp: instance.fields.addressSelfPickUp.getValue(),
                        deliveryMethod: instance.fields.deliveryMethod.getValue(),
                        deliveryCourierInterval: instance.fields.deliveryCourierInterval.getValue(),
                    };
                },
                async effect(
                    { instance, addressPickup, addressSelfPickUp, deliveryMethod, deliveryCourierInterval },
                    reactionApi
                ) {
                    await reactionApi.waitPreviousReactions();

                    instance.setContext({
                        ...instance.getContext(),
                        paymentMethods: { ...instance.getContext().paymentMethods, isLoading: true },
                    });

                    const {
                        orderId,
                        storeSlug,
                        order: { delivery_chunks: deliveryChunks },
                    } = instance.getContext();

                    const paymentMethods = await getAvailablePaymentMethods({
                        pickupId: addressPickup.pickupPointId || null,
                        pickUpWarehouseId: addressSelfPickUp.pickUpWarehouseId || null,
                        orderId,
                        pickupPointServiceType: addressPickup.serviceType,
                        deliveryMethod,
                        deliveryCourierInterval,
                        storeSlug,
                        deliveryChunks,
                    });

                    instance.setContext({
                        ...instance.getContext(),
                        paymentMethods: { items: paymentMethods, isLoading: false },
                    });

                    const currentPaymentMethod = instance.fields.paymentMethod.getValue();

                    if (paymentMethods.includes(currentPaymentMethod)) {
                        return;
                    }

                    if (
                        deliveryMethod === OrderDeliveryMethods.SELF_PICK_UP &&
                        paymentMethods.includes(OrderPaymentMethods.CARD_ON_DELIVERY)
                    ) {
                        instance.fields.paymentMethod.setValue(OrderPaymentMethods.CARD_ON_DELIVERY);

                        return;
                    }

                    const isCurrentPostPayment = OrderPostPaymentMethods.includes(currentPaymentMethod);
                    const currentPostPaymentMethods = intersection(OrderPostPaymentMethods, paymentMethods);

                    if (isCurrentPostPayment && currentPostPaymentMethods.length) {
                        instance.fields.paymentMethod.setValue(currentPostPaymentMethods[0]);
                    } else {
                        instance.fields.paymentMethod.setValue(paymentMethods[0] ?? null);
                    }
                },
            }),
        ]),
});

export type OrderFormValues = InferFormValue<typeof orderForm>;
